import { useEffect, useMemo, useState } from "react";
import {
  getChapterAddendumAssociations,
  getEpisodeAddendumAssociations,
} from "../services/api/attachments/addendum.api";
import { getAddendumId } from "../views/ReleasedTexts/ReleasedAddendumByContent/releasedAddendum.utils";

const buildLabel = (numbers) =>
  numbers
    .slice()
    .sort((a, b) => a - b)
    .map((number) => String(number).padStart(2, "0"))
    .join(", ");

/**
 * Batches the chapter/episode addendum-association lookups for an entire
 * list of addenda (instead of one API call per row) and returns a lookup
 * map keyed by addendum id, so callers can both render the associations
 * label and use the numbers as a sort key.
 *
 * @param {{ files: Array<object> }} params
 * @returns {{
 *   associationsByAddendumId: Map<number|string, { numbers: number[], label: string }>,
 *   isLoadingAssociations: boolean,
 * }}
 */
export const useAddendaAssociationsMap = ({ files = [] } = {}) => {
  const [associationsByAddendumId, setAssociationsByAddendumId] = useState(
    () => new Map()
  );
  const [isLoadingAssociations, setIsLoadingAssociations] = useState(false);

  const chapterIds = useMemo(
    () =>
      files
        .filter((file) => file?.isChapter)
        .map(getAddendumId)
        .filter((id) => id != null),
    [files]
  );

  const episodeIds = useMemo(
    () =>
      files
        .filter((file) => file?.isMaterial)
        .map(getAddendumId)
        .filter((id) => id != null),
    [files]
  );

  useEffect(() => {
    if (!chapterIds.length && !episodeIds.length) {
      setAssociationsByAddendumId(new Map());
      setIsLoadingAssociations(false);
      return;
    }

    let active = true;
    setIsLoadingAssociations(true);

    Promise.all([
      chapterIds.length
        ? getChapterAddendumAssociations({ addendumIds: chapterIds })
        : Promise.resolve([]),
      episodeIds.length
        ? getEpisodeAddendumAssociations({ addendumIds: episodeIds })
        : Promise.resolve([]),
    ])
      .then(([chapterResults, episodeResults]) => {
        if (!active) return;

        const map = new Map();

        (chapterResults ?? []).forEach((entry) => {
          const numbers = (entry?.chapters ?? [])
            .map((chapter) => chapter?.number)
            .filter((number) => number != null);

          map.set(entry.addendumId, { numbers, label: buildLabel(numbers) });
        });

        (episodeResults ?? []).forEach((entry) => {
          const numbers = (entry?.episodes ?? [])
            .map((episode) => episode?.number)
            .filter((number) => number != null);

          map.set(entry.addendumId, { numbers, label: buildLabel(numbers) });
        });

        setAssociationsByAddendumId(map);
      })
      .catch(() => {
        if (active) setAssociationsByAddendumId(new Map());
      })
      .finally(() => {
        if (active) setIsLoadingAssociations(false);
      });

    return () => {
      active = false;
    };
  }, [chapterIds, episodeIds]);

  return { associationsByAddendumId, isLoadingAssociations };
};
